import React, { useState, useEffect, useRef } from 'react';
import { Send, ArrowLeft, ArrowRight, MessageSquare } from 'lucide-react';

interface Step1 {
    url?: string;
    description?: string;
    summary?: string;
    company?: string;
}

interface Step2Props {
    businessInfo: {
        url?: string;
        description?: string;
    };
    jobDescription: string;
    onJDUpdate: (newJD: string) => void;
    onNext: () => void;
    onBack: () => void;
    parsedContent?: Step1;
    setStep2Data: (data: { jobDescription: string }) => void;
}

interface AIResponse {
    message: string;
    JD: string;
    complete: boolean;
}

interface Message {
    role: 'user' | 'assistant';
    content: string;
    timestamp: Date;
}

const TypingIndicator = () => (
    <div className="flex items-center space-x-1.5 p-3 bg-blue-50 rounded-lg max-w-fit">
        <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
        <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
        <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"></div>
    </div>
);

const Step2: React.FC<Step2Props> = ({
    businessInfo,
    jobDescription,
    onJDUpdate,
    onNext,
    onBack,
    parsedContent,
    setStep2Data
}) => {
    const [messages, setMessages] = useState<Message[]>([]);
    const [message, setMessage] = useState('');
    const [currentJD, setCurrentJD] = useState(jobDescription);
    const [isTyping, setIsTyping] = useState(false);
    const [isComplete, setIsComplete] = useState(false);
    const initialMessageSent = useRef(false);
    const messagesEndRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!initialMessageSent.current) {
            const initialMessage: Message = {
                role: 'assistant',
                content: "Hi, my name is Alex, I am here to help you write the perfect job description for" + parsedContent?.company + ". Can I please start with the role you are hiring for?",
                timestamp: new Date()
            };
            setMessages([initialMessage]);
            initialMessageSent.current = true;
        }
    }, []);

    useEffect(() => {
        if (jobDescription !== currentJD) {
            setCurrentJD(jobDescription);
            onJDUpdate(jobDescription);
        }
    }, [jobDescription, currentJD, onJDUpdate]);

    const sendMessage = async () => {
        if (!message.trim()) return;

        const userMessage: Message = {
            role: 'user',
            content: message,
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setMessage('');
        setIsTyping(true);

        try {
            const response = await fetch('/api/generate-jd', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    message,
                    businessInfo,
                    currentJD,
                    currentContent: parsedContent,
                    messageHistory: messages
                }),
            });

            const data: AIResponse = await response.json();

            const aiMessage: Message = {
                role: 'assistant',
                content: data.message,
                timestamp: new Date()
            };

            setMessages(prev => [...prev, aiMessage]);

            if (data.JD && data.JD !== currentJD) {
                setCurrentJD(data.JD);
                onJDUpdate(data.JD);
            }

            setIsComplete(data.complete);

            if (data.complete) {
                setStep2Data({ jobDescription: data.JD });
            }
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setIsTyping(false);
        }
    };

    return (
        <div className="max-w-6xl mx-auto px-4">
            <div className="bg-white rounded-lg shadow-md border border-gray-200 mb-6">
                <div className="p-6 border-b border-gray-200">
                    <h2 className="text-2xl font-semibold flex items-center gap-2">
                        <MessageSquare className="w-6 h-6" />
                        Create Job Description
                    </h2>
                </div>

                <div className="p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="space-y-4">
                            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6">
                                <h3 className="text-lg font-medium mb-2">Job Description Preview</h3>
                                <div className="prose max-w-none">
                                    <div dangerouslySetInnerHTML={{ __html: currentJD }} />
                                </div>
                            </div>

                            <div className="flex gap-4">
                                <button
                                    onClick={onBack}
                                    className="flex items-center gap-2 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                    <ArrowLeft className="w-4 h-4" />
                                    Back
                                </button>
                                <button
                                    onClick={onNext}
                                    disabled={!isComplete}
                                    className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${isComplete ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-400 cursor-not-allowed'}`}
                                >
                                    Next
                                </button>
                            </div>
                        </div>

                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col h-[600px]">
                            <div className="flex-1 p-4 overflow-y-auto space-y-4">
                                {messages.map((msg, index) => (
                                    <div
                                        key={index}
                                        className={`p-4 rounded-lg ${msg.role === 'user'
                                            ? 'bg-blue-500 text-white ml-12'
                                            : 'bg-gray-100 mr-12'
                                            }`}
                                    >
                                        {msg.content}
                                    </div>
                                ))}
                                {isTyping && <TypingIndicator />}
                                <div ref={messagesEndRef} />
                            </div>

                            <div className="border-t p-4 bg-gray-50">
                                <div className="flex items-center space-x-2">
                                    <input
                                        type="text"
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                                        className="flex-1 rounded-lg border border-gray-300 p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                        placeholder="Type your message..."
                                    />
                                    <button
                                        onClick={sendMessage}
                                        className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                    >
                                        <Send className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div >
    );
};

export default Step2;